import { useState, useEffect } from 'react';
import {
  X, Edit2, Loader2, FileText, History, Trash2, Download, Package,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { purchasingAPI, ContractPrice, ContractPricePart } from '../../lib/api';
import { toast } from 'sonner';
import { useConfirm } from '../../contexts/ConfirmDialogContext';
import { usePreferences } from '../../contexts/PreferencesContext';
import { usePortalMode } from '../../hooks/usePortalMode';
// CR-6424: R2/R3 sub-components live in their own files (500-line cap).
import ContractPriceHistoryTab from './ContractPriceHistoryTab';
import ContractPricebookEditForm from './ContractPricebookEditForm';
import { useContractChangelog, CONTRACT_LOGS_PAGE_SIZE } from './useContractChangelog';
import { descriptionHint } from './contractPartDescription';
// CR-7279: the PO Line pop-up's Pricebook tab already sorts these same columns (CR-7263).
import { useColumnSort } from '../../hooks/useColumnSort';
import { SortableTh } from '../common/SortableTh';

const NO_PARTS: NonNullable<ContractPrice['parts']> = [];
const PART_NUMERIC_FIELDS = ['qty_from', 'qty_to', 'unit_price', 'lot_price'] as const;
// A part with no description anywhere comes back as '' and shows a dash; treat
// it as blank so it stays last in both directions, like the blank prices.
const PART_SORT_COMPUTED = {
  description: (p: ContractPricePart) => p.description || null,
};

interface ContractPriceDetailModalProps {
  contractId: number;
  isOpen: boolean;
  onClose: () => void;
  /** Optional edit handler. When not provided, Edit/Delete buttons are hidden (read-only mode). */
  onEdit?: (contract: ContractPrice) => void;
  // CR-5975: hide the Parts sub-tab when the modal is opened from the
  // high-level Contracts module (Suppliers > Contracts). Parts belong
  // to Price Books, not contract-award management.
  hidePartsTab?: boolean;
  // CR-6424: pricebook mode for the Purchasing > Pricing tab. Presents this as
  // a Contracted-Prices / pricebook contract rather than a supplier-relationship
  // contract — hides the relationship-only fields (owner, auto-renew, risk,
  // notice/reminder, BR frequency, negotiation, contract file), labels the tabs
  // Edit Contract / Contract Parts / Contract Logs, and shows Active as "Live".
  // The Suppliers > Contracts view leaves this false to keep the full view.
  pricebookMode?: boolean;
  // CR-6037 rework: parent bumps this on successful save in the edit modal
  // so this still-open detail view refetches and shows the updated parts
  // without a manual page refresh.
  refreshTick?: number;
  // Fired after a successful delete so the parent list can refetch — without
  // it the deleted contract stayed visible in the table until manual reload
  // (same stale-parent shape as the CR-6037 save-side bug).
  onDeleted?: () => void;
  // CR-6424 (R3): fired after a successful save in the inline Edit Contract
  // form so the parent list refetches (same shape as onDeleted).
  onSaved?: () => void;
}

export default function ContractPriceDetailModal({
  contractId,
  isOpen,
  onClose,
  onEdit,
  hidePartsTab = false,
  pricebookMode = false,
  refreshTick = 0,
  onDeleted,
  onSaved,
}: ContractPriceDetailModalProps) {
  const { formatDate } = usePreferences();
  const { isSupplier } = usePortalMode();
  const confirm = useConfirm();
  const [loading, setLoading] = useState(true);
  const [contract, setContract] = useState<ContractPrice | null>(null);
  const [activeTab, setActiveTab] = useState('details');
  const [deleting, setDeleting] = useState(false);
  // Customer review 11 Sep (item 21): the trail + its derived first entry.
  // CR-6346: suppliers are refused the changelog, so it is never fetched for them.
  const changelog = useContractChangelog(contractId, isOpen && !isSupplier, refreshTick);
  const logsTotal = changelog.count + (changelog.derivedCreated ? 1 : 0);
  // CR-7279: every column on Contract Parts sorts. All parts arrive in one
  // payload, so a client-side sort covers the full list. Unsorted keeps the
  // server's order.
  const { sortedRows: sortedParts, sortField, sortDirection, onSort } = useColumnSort(
    contract?.parts ?? NO_PARTS,
    { numericFields: PART_NUMERIC_FIELDS, computed: PART_SORT_COMPUTED },
  );
  const sortProps = { sortField, sortDirection, onSort };
  // SortableTh puts each label in a <button>, and Tailwind resets text-transform
  // on buttons, so the labels keep the header row's uppercase themselves.

  useEffect(() => {
    if (isOpen && contractId) {
      fetchContractDetails();
    }
    // CR-6037 rework: refreshTick is incremented by the parent after a
    // successful save in the overlaid edit modal — refetching here is what
    // makes the newly added/edited part appear without a hard reload.
  }, [isOpen, contractId, refreshTick]);

  const fetchContractDetails = async () => {
    try {
      setLoading(true);
      const contractRes = await purchasingAPI.getContractPrice(contractId);
      setContract(contractRes.data);
    } catch (err: unknown) {
      console.error('Failed to load contract details:', err);
      toast.error('Failed to load contract details');
    } finally {
      setLoading(false);
    }
  };

  // CR-6424 (R3): after an inline save — refresh this modal and the parent.
  const handleInlineSaved = () => {
    fetchContractDetails();
    changelog.refresh();
    onSaved?.();
  };

  const handleDelete = async () => {
    if (!contract) return;

    const ok = await confirm({ title: 'Delete Contract', description: `Are you sure you want to delete contract "${contract.contract_number}"? This action cannot be undone.`, confirmLabel: 'Delete', variant: 'destructive' });
    if (!ok) {
      return;
    }

    setDeleting(true);
    try {
      await purchasingAPI.deleteContractPrice(contract.id);
      toast.success('Contract deleted successfully');
      onDeleted?.();
      onClose();
    } catch (err: unknown) {
      console.error('Failed to delete contract:', err);
      toast.error('Failed to delete contract');
    } finally {
      setDeleting(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { label: string; className: string }> = {
      draft: { label: 'Draft', className: 'bg-gray-100 text-gray-800' },
      // CR-6424: the pricebook area uses the legacy "Live" label for active.
      active: { label: pricebookMode ? 'Live' : 'Active', className: 'bg-green-100 text-green-800' },
      expired: { label: 'Expired', className: 'bg-red-100 text-red-800' },
    };
    const config = statusConfig[status] || { label: status, className: 'bg-gray-100 text-gray-800' };
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${config.className}`}>
        {config.label}
      </span>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-3">
            <FileText className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold">
              Contract Details
            </h2>
            {contract && getStatusBadge(contract.status)}
          </div>
          {/* CR-6415: Edit/Delete moved out of the header into the footer's
              bottom-left action area — the header now carries only the close
              affordance, matching the other detail pop-ups. */}
          <button onClick={onClose} className="p-1 hover:bg-muted rounded">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content — CR-4878: this wrapper and its Tabs/scroll descendants
            must all be flex-col with min-h-0 so overflow-y-auto actually
            scrolls when content exceeds the modal's max-h-[90vh]. Using
            `h-full` on the Tabs didn't work because the parent's computed
            height comes from the flex algorithm, not an explicit size, so
            Tabs grew to its content (~769px) and spilled past the modal
            footer — clipping the Notes section. Making the wrapper itself
            a flex-col and giving Tabs flex-1 forces the flex algorithm to
            constrain Tabs to the remaining space. */}
        <div className="flex-1 overflow-hidden min-h-0 flex flex-col">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : contract ? (
            <Tabs
              // CR-6424 review: the Parts trigger is hidden when hidePartsTab,
              // but the controlled activeTab can persist as 'parts' across a
              // close/reopen — fall back to Details so a hidden tab's content
              // never renders.
              value={hidePartsTab && activeTab === 'parts' ? 'details' : activeTab}
              onValueChange={setActiveTab}
              className="flex-1 flex flex-col min-h-0"
            >
              <div className="border-b px-4">
                <TabsList>
                  <TabsTrigger value="details" className="gap-2">
                    <FileText className="h-4 w-4" />
                    {/* CR-6346: "Edit Contract" is the customer-side pricebook
                        label. A supplier reaches this modal read-only, so name
                        the tab for what they can actually do. */}
                    {pricebookMode && !isSupplier ? 'Edit Contract' : 'Details'}
                  </TabsTrigger>
                  {!hidePartsTab && (
                    <TabsTrigger value="parts" className="gap-2">
                      <Package className="h-4 w-4" />
                      {pricebookMode ? 'Contract Parts' : 'Parts'} ({contract.parts?.length ?? 0})
                    </TabsTrigger>
                  )}
                  {/* CR-6346: suppliers are not served the changelog — hide
                      the tab rather than offer an empty one. CR-6424 (R2): the
                      SERVER's total, plus the derived "Created by" entry. */}
                  {!isSupplier && (
                    <TabsTrigger value="history" className="gap-2">
                      <History className="h-4 w-4" />
                      {pricebookMode ? 'Contract Logs' : 'History'} ({logsTotal})
                    </TabsTrigger>
                  )}
                </TabsList>
              </div>

              <div className="flex-1 overflow-y-auto p-6 min-h-0">
                <TabsContent value="details" className="m-0">
                  {/* CR-6424 (R3): in pricebook mode, for a viewer with edit
                      rights, the "Edit Contract" tab IS an edit form (the
                      legacy pop-up edits inline on this tab). Suppliers and
                      read-only mounts keep the display grid below. */}
                  {pricebookMode && !isSupplier && onEdit && contract ? (
                    <ContractPricebookEditForm
                      key={`${contract.id}-${contract.updated_at}`}
                      contract={contract}
                      onSaved={handleInlineSaved}
                    />
                  ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <div>
                      <label className="text-xs text-muted-foreground uppercase tracking-wider">Contract Number</label>
                      <p className="font-mono font-semibold text-lg">{contract.contract_number}</p>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground uppercase tracking-wider">Customer</label>
                      <p className="font-medium">{contract.customer_name || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground uppercase tracking-wider">Supplier</label>
                      <p className="font-medium">{contract.supplier_name || 'N/A'}</p>
                    </div>
                    <div>
                      {/* Customer review 11 Sep (item 23) */}
                      <label className="text-xs text-muted-foreground uppercase tracking-wider">Price Book / Contract Type</label>
                      <p className="font-medium">{contract.contract_type_name ? `${contract.contract_type_name} (${contract.contract_type_abbreviation})` : 'Not set'}</p>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground uppercase tracking-wider">Valid From</label>
                      <p className="font-medium">{formatDate(contract.valid_from)}</p>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground uppercase tracking-wider">Valid To</label>
                      <p className="font-medium">{formatDate(contract.valid_to)}</p>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground uppercase tracking-wider">Currency</label>
                      <p className="font-medium">{contract.currency}</p>
                    </div>
                    {/* CR-6424: supplier-relationship fields — hidden in the
                        pricebook (Purchasing > Pricing) view, which manages only
                        contracted-price data. Still shown in Suppliers > Contracts. */}
                    {!pricebookMode && (
                      <>
                    <div>
                      <label className="text-xs text-muted-foreground uppercase tracking-wider">Contract Owner</label>
                      <p className="font-medium">{contract.owner_name || contract.owner_details?.full_name || 'Not assigned'}</p>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground uppercase tracking-wider">Auto-Renew</label>
                      <p className={`font-medium ${contract.auto_renew ? 'text-green-600' : ''}`}>
                        {contract.auto_renew ? 'Yes' : 'No'}
                        {contract.auto_renew && contract.renewal_period_years ? ` (${contract.renewal_period_years}yr)` : ''}
                      </p>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground uppercase tracking-wider">Risk Category</label>
                      <p className="font-medium capitalize">{contract.risk_category || 'Not set'}</p>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground uppercase tracking-wider">Notice Period</label>
                      <p className="font-medium">
                        {contract.notice_period_months != null
                          ? `${contract.notice_period_months} ${contract.notice_period_months === 1 ? 'month' : 'months'}`
                          : 'Not set'}
                      </p>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground uppercase tracking-wider">Reminder (days before expiry)</label>
                      <p className="font-medium">
                        {contract.reminder_days != null
                          ? `${contract.reminder_days} ${contract.reminder_days === 1 ? 'day' : 'days'}`
                          : 'Not set'}
                      </p>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground uppercase tracking-wider">BR Frequency</label>
                      <p className="font-medium">
                        {contract.br_frequency_months != null
                          ? `${contract.br_frequency_months} ${contract.br_frequency_months === 1 ? 'month' : 'months'}`
                          : 'Not set'}
                      </p>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground uppercase tracking-wider">Contract Negotiation</label>
                      <p className="font-medium">{contract.contract_negotiation ? 'Yes' : 'No'}</p>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground uppercase tracking-wider">Contract File</label>
                      {contract.contract_file ? (
                        // CR-4880: Route through the authenticated API
                        // download proxy instead of linking to the raw
                        // contract_file URL. The direct S3 link was failing
                        // to open on staging (no click action). The
                        // proxy endpoint handles auth + streams the file
                        // regardless of the underlying storage shape.
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              const response = await purchasingAPI.downloadContractFile(contract.id);
                              const contentType = response.headers?.['content-type'];
                              const blob = new Blob([response.data], {
                                type: typeof contentType === 'string' ? contentType : 'application/octet-stream',
                              });
                              const url = window.URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = contract.contract_file!.split('/').pop() || 'contract';
                              document.body.appendChild(a);
                              a.click();
                              a.remove();
                              window.URL.revokeObjectURL(url);
                            } catch {
                              toast.error('Failed to download contract file');
                            }
                          }}
                          className="font-medium text-primary hover:underline flex items-center gap-1"
                        >
                          <Download className="h-3 w-3" />
                          {contract.contract_file.split('/').pop()}
                        </button>
                      ) : (
                        <p className="font-medium text-muted-foreground">No file uploaded</p>
                      )}
                    </div>
                      </>
                    )}
                    {/* The supplier's price-book payload has no creator (CR-6346) —
                        omit the row rather than print "N/A" beside a log that names them. */}
                    {contract.created_by_name !== undefined && (
                    <div>
                      <label className="text-xs text-muted-foreground uppercase tracking-wider">Created By</label>
                      <p className="font-medium">{contract.created_by_name || 'N/A'}</p>
                    </div>
                    )}
                    <div>
                      <label className="text-xs text-muted-foreground uppercase tracking-wider">Created At</label>
                      <p className="font-medium">{formatDate(contract.created_at)}</p>
                    </div>
                    {/* CR-4879: Total Parts field removed from the high-level
                        contract detail view — parts count lives on the
                        dedicated parts breakdown, not this summary. */}
                    {contract.notes && (
                      <div className="md:col-span-2 lg:col-span-3">
                        <label className="text-xs text-muted-foreground uppercase tracking-wider block">Notes</label>
                        {/* CR-4878: whitespace-pre-wrap + break-words keep
                            long or multi-line notes readable inside the
                            pop-up instead of overflowing horizontally. */}
                        <p className="text-sm mt-1 p-3 bg-muted/30 rounded-lg whitespace-pre-wrap break-words">{contract.notes}</p>
                      </div>
                    )}
                  </div>
                  )}
                </TabsContent>

                <TabsContent value="parts" className="m-0">
                  {(contract.parts?.length ?? 0) > 0 ? (
                    <div className="overflow-x-auto border rounded-lg">
                      <table className="w-full text-sm">
                        <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                          <tr>
                            <SortableTh field="part_number" {...sortProps} align="left" className="text-left px-3 py-2 font-medium"><span className="uppercase">Part Number</span></SortableTh>
                            <SortableTh field="drawing_issue" {...sortProps} align="left" className="text-left px-3 py-2 font-medium"><span className="uppercase">Drawing Issue</span></SortableTh>
                            <SortableTh field="description" {...sortProps} align="left" className="text-left px-3 py-2 font-medium"><span className="uppercase">Description</span></SortableTh>
                            <SortableTh field="qty_from" {...sortProps} align="right" className="text-right px-3 py-2 font-medium"><span className="uppercase">Qty From</span></SortableTh>
                            <SortableTh field="qty_to" {...sortProps} align="right" className="text-right px-3 py-2 font-medium"><span className="uppercase">Qty To</span></SortableTh>
                            <SortableTh field="unit_price" {...sortProps} align="right" className="text-right px-3 py-2 font-medium"><span className="uppercase">Unit Price</span></SortableTh>
                            <SortableTh field="lot_price" {...sortProps} align="right" className="text-right px-3 py-2 font-medium"><span className="uppercase">Lot Price</span></SortableTh>
                            <SortableTh field="unit_of_measure" {...sortProps} align="left" className="text-left px-3 py-2 font-medium"><span className="uppercase">UoM</span></SortableTh>
                          </tr>
                        </thead>
                        <tbody>
                          {sortedParts.map((p) => (
                            <tr key={p.id ?? `${p.part_number}-${p.qty_from}-${p.qty_to}`} className="border-t">
                              <td className="px-3 py-2 font-mono">{p.part_number}</td>
                              {/* CR-7387: part number + drawing issue identify the part. */}
                              <td className="px-3 py-2 font-mono">{p.drawing_issue || '—'}</td>
                              <td className="px-3 py-2 whitespace-pre-line" title={descriptionHint(p.description_source)}>{p.description || '—'}</td>
                              <td className="px-3 py-2 text-right tabular-nums">{p.qty_from}</td>
                              <td className="px-3 py-2 text-right tabular-nums">{p.qty_to}</td>
                              <td className="px-3 py-2 text-right tabular-nums">
                                {p.unit_price ? `${contract.currency} ${p.unit_price}` : '—'}
                              </td>
                              <td className="px-3 py-2 text-right tabular-nums">
                                {p.lot_price ? `${contract.currency} ${p.lot_price}` : '—'}
                              </td>
                              <td className="px-3 py-2">{p.unit_of_measure || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-12 bg-muted/30 rounded-lg">
                      <Package className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                      <p className="text-muted-foreground">No parts on this contract yet</p>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="history" className="m-0">
                  {/* CR-5973 layout, extracted for CR-6424 (R2): pages
                      through the full server-side trail, not page 1 only. */}
                  <ContractPriceHistoryTab
                    logs={changelog.logs}
                    totalCount={changelog.count}
                    derivedCreated={changelog.derivedCreated}
                    currentPage={changelog.page}
                    pageSize={CONTRACT_LOGS_PAGE_SIZE}
                    onPageChange={changelog.setPage}
                  />
                </TabsContent>
              </div>
            </Tabs>
          ) : (
            <div className="text-center py-12">
              <p className="text-muted-foreground">Contract not found</p>
            </div>
          )}
        </div>

        {/* Footer — CR-6415: primary record actions sit bottom-left, the
            dismiss action stays bottom-right. The spacer keeps Close pinned
            right even in read-only mode, where the left group renders nothing. */}
        <div className="flex items-center gap-3 p-4 border-t">
          {/* Only show Edit/Delete buttons when onEdit handler is provided (not read-only mode) */}
          {contract && onEdit && (
            <>
              <Button
                variant="outline"
                onClick={() => onEdit(contract)}
              >
                <Edit2 className="h-4 w-4 mr-1" />
                Edit
              </Button>
              <Button
                variant="outline"
                size="icon"
                aria-label="Delete contract"
                title="Delete contract"
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
              </Button>
            </>
          )}
          <div className="flex-1" />
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}
